import { useEffect, useMemo, useState } from "react";
import Fuse from "fuse.js";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, DollarSign, Pencil, Plus, Trash2, Save, X, Loader2, ChevronUp, ChevronDown, Search } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL } from "../lib/constants";
import { toast } from "sonner";

const PRICE_LABELS = ["Corp Price", "Ed / Gov't / Non-Profit", "Value"];

// Column labels for the tier tables. Most categories use the default
// (Qty / Corp / Ed / Gov't / NP). Facilitator Materials repurposes the two
// price columns as Standard vs Premier access tiers, so we relabel them
// there for clarity without touching the underlying data model.
const TIER_LABEL_OVERRIDES = {
  "Facilitator Materials": { qty: "Item", corp: "Standard", ed: "Premier" },
};
const DEFAULT_TIER_LABELS = { qty: "Qty", corp: "Corp", ed: "Ed / Gov't / NP" };
const tierLabelsFor = (cat) => TIER_LABEL_OVERRIDES[cat] || DEFAULT_TIER_LABELS;

// True Colors palette cycled across categories for visual chunking
const CATEGORY_PALETTE = ["#f68b1f", "#0091be", "#7ac143", "#ffd400", "#083050"];
const colorForCategory = (cat, allCategories) => {
  const idx = Math.max(0, allCategories.indexOf(cat));
  return CATEGORY_PALETTE[idx % CATEGORY_PALETTE.length];
};

const emptyItem = {
  category: "",
  subcategory: "",
  name: "",
  description: "",
  notes: "",
  tiers: [{ pkg_qty: "", corp_price: "", ed_govt_price: "", value_price: "" }],
  order: 999,
};

export default function Pricing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [collapsed, setCollapsed] = useState({}); // {category: true} when collapsed
  const [search, setSearch] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/pricing").catch(() => ({ data: [] })),
      api.get("/pricing/categories").catch(() => ({ data: [] })),
    ]).then(([{ data: a }, { data: b }]) => {
      setItems(Array.isArray(a) ? a : []);
      setCategories(Array.isArray(b) ? b : []);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Fuzzy + acronym-aware search. We pre-compute an `acronym` field for every
  // item (e.g. "Personal Awareness and Success Training" → "pas+pas") so reps
  // can type abbreviations and still find things. Fuse handles typo tolerance.
  const indexed = useMemo(() => items.map((it) => ({
    ...it,
    acronym: (it.name || "")
      .split(/\s+/)
      .map((w) => w.replace(/[^A-Za-z]/g, "")[0] || "")
      .join("")
      .toLowerCase(),
    tier_blob: (it.tiers || [])
      .map((t) => `${t.pkg_qty || ""} ${t.corp_price || ""} ${t.ed_govt_price || ""}`)
      .join(" "),
  })), [items]);

  const fuse = useMemo(() => new Fuse(indexed, {
    keys: [
      { name: "name", weight: 3 },
      { name: "acronym", weight: 3 },
      { name: "category", weight: 1 },
      { name: "subcategory", weight: 1 },
      { name: "description", weight: 1 },
      { name: "notes", weight: 1 },
      { name: "tier_blob", weight: 0.5 },
    ],
    threshold: 0.4,            // 0 = exact, 1 = match anything; 0.4 is generous-but-sane
    ignoreLocation: true,      // match anywhere in the field
    minMatchCharLength: 2,
    includeScore: true,
  }), [indexed]);

  // When the user is searching, we (a) sort items by Fuse score so the closest
  // match is up top, and (b) reorder categories so the category containing the
  // best match shows first. When not searching, we keep the natural category order.
  const { grouped, categoryOrder } = useMemo(() => {
    const q = search.trim();
    if (!q) {
      const map = {};
      items.forEach((it) => {
        const cat = it.category || "Other";
        if (!map[cat]) map[cat] = [];
        map[cat].push(it);
      });
      return { grouped: map, categoryOrder: null };
    }
    const results = fuse.search(q); // already sorted best-first
    const map = {};
    const firstSeenScore = {};
    results.forEach(({ item, score }) => {
      const cat = item.category || "Other";
      if (!map[cat]) {
        map[cat] = [];
        firstSeenScore[cat] = score;
      }
      map[cat].push(item);
    });
    const order = Object.keys(map).sort((a, b) => firstSeenScore[a] - firstSeenScore[b]);
    return { grouped: map, categoryOrder: order };
  }, [items, search, fuse]);

  // Keep every accordion CLOSED at the START of a new search so the user can
  // scan a tidy list of names. Once collapsed, the user can click any header
  // to expand that one — that's why we DON'T force-override the toggle here.
  // We watch `search` and reset the open/closed state each time the query
  // changes, then hand control back to the user.
  const isSearching = !!search.trim();
  useEffect(() => {
    if (isSearching) {
      // Build a "everything collapsed" snapshot from the current grouped
      // categories so that the user starts fresh on each new query.
      const cats = Object.keys(grouped);
      setCollapsed(cats.reduce((acc, c) => ({ ...acc, [c]: true }), {}));
    } else {
      // Clear collapsed state when the search clears so categories reopen.
      setCollapsed({});
    }
    // grouped is intentionally NOT in deps — we only want to recalc on
    // search change, otherwise re-running this would slam collapsed shut
    // every time the user toggles a section.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);
  const effectiveCollapsed = collapsed;

  const startEdit = (item) => {
    setEditingId(item.id);
    setDraft({
      ...item,
      tiers: item.tiers?.length ? item.tiers.map((t) => ({ ...t })) : [{ pkg_qty: "", corp_price: "", ed_govt_price: "", value_price: "" }],
    });
  };
  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveEdit = async () => {
    try {
      if (editingId === "new") {
        await api.post("/pricing", draft);
        toast.success("Item added.");
      } else {
        await api.put(`/pricing/${editingId}`, draft);
        toast.success("Saved.");
      }
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const deleteItem = async (id) => {
    if (!confirm("Delete this pricing item?")) return;
    try {
      await api.delete(`/pricing/${id}`);
      toast.success("Deleted.");
      load();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const startNew = (category = "") => {
    setEditingId("new");
    setDraft({ ...emptyItem, category });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="pricing-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-blue-700">Pricing</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Programs, packages, and what to quote.</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Internal pricing for every TCI offering. {isAdmin ? "Tap the pencil to edit any line." : "Talk to Theresa to update."}</p>
        </div>
        {isAdmin && (
          <button
            onClick={() => startNew()}
            data-testid="pricing-add-item"
            className="h-11 px-4 rounded-2xl bg-tc-ink text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
          >
            <Plus size={16} /> Add item
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-slate-400 py-12 text-center">Loading…</div>
      ) : (
        <>
          {/* Search bar */}
          <div className="mt-6 relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by item name, category, notes…"
              data-testid="pricing-search"
              className="w-full h-12 rounded-2xl bg-white border-2 border-slate-200 focus:border-orange-500 outline-none pl-11 pr-12 text-sm font-medium shadow-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                data-testid="pricing-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-slate-100 text-slate-400"
                title="Clear"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {Object.keys(grouped).length === 0 && search ? (
            <div className="tc-card p-10 text-center text-slate-500 mt-4" data-testid="pricing-no-results">
              <Search size={32} className="mx-auto text-slate-300" />
              <div className="font-display text-xl text-tc-ink mt-3">No matches for &ldquo;{search}&rdquo;</div>
              <div className="text-sm mt-1">Try a different word or clear the search.</div>
            </div>
          ) : (
            <div className="mt-4 space-y-5">
          {(categoryOrder
              ? categoryOrder
              : categories.filter((c) => grouped[c]?.length).concat(Object.keys(grouped).filter((k) => !categories.includes(k)))
          ).map((cat) => {
            const allCats = categories.length ? categories : Object.keys(grouped);
            const catColor = colorForCategory(cat, allCats);
            return (
            <section key={cat} className="tc-card overflow-hidden" data-testid={`pricing-section-${cat.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
              <button
                onClick={() => setCollapsed((c) => ({ ...c, [cat]: !c[cat] }))}
                className="w-full flex items-center justify-between p-4 sm:p-5 hover:bg-slate-50 transition-colors"
              >
                <h2 className="font-display text-xl sm:text-2xl text-tc-ink flex items-center gap-2.5">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: catColor }} />
                  {cat}
                  <span className="text-sm font-bold text-slate-400">({grouped[cat]?.length || 0})</span>
                </h2>
                <motion.span animate={{ rotate: effectiveCollapsed[cat] ? 180 : 0 }} transition={{ duration: 0.2 }}><ChevronUp size={18} className="text-slate-400" /></motion.span>
              </button>
              <AnimatePresence initial={false}>
                {!effectiveCollapsed[cat] && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="border-t border-slate-100 p-3 sm:p-4 space-y-2.5 bg-slate-50/40">
                      {grouped[cat].map((item) => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          color={catColor}
                          isAdmin={isAdmin}
                          isEditing={editingId === item.id}
                          draft={editingId === item.id ? draft : null}
                          setDraft={setDraft}
                          onStartEdit={() => startEdit(item)}
                          onCancel={cancelEdit}
                          onSave={saveEdit}
                          onDelete={() => deleteItem(item.id)}
                          forceOpen={!!search.trim()}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </section>
            );
          })}
            </div>
          )}
        </>
      )}

      {editingId === "new" && draft && (
        <NewItemModal
          draft={draft}
          setDraft={setDraft}
          categories={categories}
          onCancel={cancelEdit}
          onSave={saveEdit}
        />
      )}
    </div>
  );
}

function ItemCard({ item, color, isAdmin, isEditing, draft, setDraft, onStartEdit, onCancel, onSave, onDelete, forceOpen }) {
  const [open, setOpen] = useState(false);
  const isOpen = isEditing || open || forceOpen;
  const hasPricing = item.tiers?.length > 0;

  if (isEditing) {
    return (
      <div className="bg-white rounded-xl border-2 overflow-hidden" style={{ borderColor: color }}>
        <div className="h-1.5 w-full" style={{ backgroundColor: color }} />
        <EditingRow draft={draft} setDraft={setDraft} onCancel={onCancel} onSave={onSave} />
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-xl border-2 overflow-hidden transition-colors"
      style={{ borderColor: isOpen ? color : "#e2e8f0" }}
      data-testid={`pricing-item-${item.id}`}
    >
      <div className="h-1.5 w-full" style={{ backgroundColor: color }} />
      <div className="flex items-center gap-1 pr-2 sm:pr-3 group">
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid={`pricing-toggle-${item.id}`}
          aria-expanded={isOpen}
          className="flex-1 text-left p-3 sm:p-4 flex items-center gap-3 hover:bg-slate-50/60 transition-colors min-w-0"
        >
          <div className="flex-1 min-w-0">
            {item.subcategory && (
              <div className="text-[10px] font-extrabold uppercase tracking-widest" style={{ color }}>
                {item.subcategory}
              </div>
            )}
            <div className="font-bold text-tc-ink leading-snug truncate">{item.name}</div>
            {!isOpen && item.description && (
              <div className="text-xs text-slate-500 mt-0.5 truncate">{item.description}</div>
            )}
          </div>
          {!isOpen && hasPricing && (
            <span className="hidden sm:inline-flex text-[11px] font-bold text-slate-500 bg-slate-100 rounded-full px-2.5 py-1 shrink-0">
              {item.tiers.length} {item.tiers.length === 1 ? "tier" : "tiers"}
            </span>
          )}
          <ChevronDown
            size={18}
            className="text-slate-400 shrink-0 transition-transform"
            style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </button>
        {isAdmin && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button onClick={(e) => { e.stopPropagation(); onStartEdit(); }} data-testid={`pricing-edit-${item.id}`} className="w-8 h-8 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500 hover:text-tc-ink" title="Edit">
              <Pencil size={13} />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} data-testid={`pricing-delete-${item.id}`} className="w-8 h-8 rounded-lg hover:bg-red-50 inline-flex items-center justify-center text-slate-400 hover:text-red-500" title="Delete">
              <Trash2 size={13} />
            </button>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="overflow-hidden border-t border-slate-100"
          >
            <div className="p-4 sm:p-5 space-y-3">
              {item.description && (
                <div className="text-sm text-slate-600 leading-relaxed">{item.description}</div>
              )}
              {item.notes && (
                <div className="text-xs text-slate-400 italic leading-relaxed">{item.notes}</div>
              )}
              {hasPricing && (
                <div className="overflow-x-auto rounded-xl border border-slate-100 bg-slate-50/50">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-slate-500 font-bold border-b border-slate-200">
                        <th className="text-left px-3 py-2 font-bold w-[30%]">{tierLabelsFor(item.category).qty}</th>
                        <th className="text-left px-3 py-2 font-bold w-[35%]">{tierLabelsFor(item.category).corp}</th>
                        <th className="text-left px-3 py-2 font-bold w-[35%]">{tierLabelsFor(item.category).ed}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {item.tiers.map((t, i) => (
                        <tr key={i} className="border-t border-slate-100 first:border-0">
                          <td className="px-3 py-2 font-bold text-slate-700">{t.pkg_qty || "—"}</td>
                          <td className="px-3 py-2 text-tc-ink">{t.corp_price || "—"}</td>
                          <td className="px-3 py-2 text-tc-ink">{t.ed_govt_price || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!hasPricing && (
                <div className="text-xs text-slate-400 italic">No pricing tiers set.</div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ItemRow({ item, isAdmin, isEditing, draft, setDraft, onStartEdit, onCancel, onSave, onDelete }) {
  if (isEditing) {
    return <EditingRow draft={draft} setDraft={setDraft} onCancel={onCancel} onSave={onSave} />;
  }
  return (
    <div className="px-4 sm:px-5 py-4 border-b border-slate-100 last:border-0 group" data-testid={`pricing-item-${item.id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          {item.subcategory && <div className="tc-eyebrow text-blue-600">{item.subcategory}</div>}
          <div className="font-bold text-tc-ink leading-snug">{item.name}</div>
          {item.description && <div className="text-sm text-slate-500 mt-1">{item.description}</div>}
          {item.notes && <div className="text-xs text-slate-400 mt-1.5 italic leading-relaxed">{item.notes}</div>}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={onStartEdit} data-testid={`pricing-edit-${item.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title="Edit">
              <Pencil size={14} />
            </button>
            <button onClick={onDelete} data-testid={`pricing-delete-${item.id}`} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>

      {item.tiers?.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
                <th className="text-left py-1.5 font-bold w-[30%]">{tierLabelsFor(item.category).qty}</th>
                <th className="text-left py-1.5 font-bold w-[35%]">{tierLabelsFor(item.category).corp}</th>
                <th className="text-left py-1.5 font-bold w-[35%]">{tierLabelsFor(item.category).ed}</th>
              </tr>
            </thead>
            <tbody>
              {item.tiers.map((t, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1.5 font-bold text-slate-600">{t.pkg_qty || ","}</td>
                  <td className="py-1.5 text-tc-ink">{t.corp_price || ","}</td>
                  <td className="py-1.5 text-tc-ink">{t.ed_govt_price || ","}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function EditingRow({ draft, setDraft, onCancel, onSave }) {
  const updateTier = (i, k, v) => {
    const next = draft.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t);
    setDraft({ ...draft, tiers: next });
  };
  const addTier = () => setDraft({ ...draft, tiers: [...draft.tiers, { pkg_qty: "", corp_price: "", ed_govt_price: "", value_price: "" }] });
  const removeTier = (i) => setDraft({ ...draft, tiers: draft.tiers.filter((_, idx) => idx !== i) });

  return (
    <div className="bg-orange-50 px-4 sm:px-5 py-4 border-b border-slate-100" data-testid="pricing-editing-row">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Subcategory"><input value={draft.subcategory || ""} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} className={INPUT_CLS} /></Field>
        <Field label="Name"><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={INPUT_CLS} /></Field>
        <Field label="Description" full><input value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className={INPUT_CLS} /></Field>
        <Field label="Notes" full><textarea rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className={INPUT_CLS} /></Field>
      </div>

      <div className="mt-4">
        <div className="tc-eyebrow text-slate-500 mb-1.5">Pricing tiers</div>
        <div className="space-y-2">
          {draft.tiers.map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 items-center">
              <input placeholder="Qty (e.g. 10,20)" value={t.pkg_qty} onChange={(e) => updateTier(i, "pkg_qty", e.target.value)} className={`col-span-3 ${INPUT_CLS}`} />
              <input placeholder="Corp price" value={t.corp_price} onChange={(e) => updateTier(i, "corp_price", e.target.value)} className={`col-span-4 ${INPUT_CLS}`} />
              <input placeholder="Ed/Gov/NP price" value={t.ed_govt_price} onChange={(e) => updateTier(i, "ed_govt_price", e.target.value)} className={`col-span-4 ${INPUT_CLS}`} />
              <button onClick={() => removeTier(i)} className="col-span-1 p-2 rounded-lg hover:bg-red-50 text-red-500 justify-self-end" title="Remove tier"><X size={14} /></button>
            </div>
          ))}
        </div>
        <button onClick={addTier} className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-blue-600 hover:text-blue-800"><Plus size={14} /> Add tier</button>
      </div>

      <div className="mt-4 flex items-center gap-2 justify-end">
        <button onClick={onCancel} className="px-4 h-10 rounded-xl font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
        <button onClick={onSave} data-testid="pricing-save-button" className="px-4 h-10 rounded-xl bg-green-500 text-white font-bold flex items-center gap-2"><Save size={14} /> Save</button>
      </div>
    </div>
  );
}

function NewItemModal({ draft, setDraft, categories, onCancel, onSave }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onCancel}>
      <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-7 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-2xl text-tc-ink">Add a pricing item</h2>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <Field label="Category">
            <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className={INPUT_CLS}>
              <option value="">Choose…</option>
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Subcategory"><input value={draft.subcategory || ""} onChange={(e) => setDraft({ ...draft, subcategory: e.target.value })} className={INPUT_CLS} /></Field>
          <Field label="Name" full><input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={INPUT_CLS} /></Field>
          <Field label="Notes" full><textarea rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className={INPUT_CLS} /></Field>
        </div>
        <div className="mt-4">
          <div className="tc-eyebrow text-slate-500 mb-1.5">Pricing tiers</div>
          <div className="space-y-2">
            {draft.tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input placeholder="Qty" value={t.pkg_qty} onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((x,idx) => idx===i ? { ...x, pkg_qty: e.target.value } : x) })} className={`col-span-3 ${INPUT_CLS}`} />
                <input placeholder="Corp price" value={t.corp_price} onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((x,idx) => idx===i ? { ...x, corp_price: e.target.value } : x) })} className={`col-span-4 ${INPUT_CLS}`} />
                <input placeholder="Ed/Gov/NP price" value={t.ed_govt_price} onChange={(e) => setDraft({ ...draft, tiers: draft.tiers.map((x,idx) => idx===i ? { ...x, ed_govt_price: e.target.value } : x) })} className={`col-span-5 ${INPUT_CLS}`} />
              </div>
            ))}
          </div>
          <button onClick={() => setDraft({ ...draft, tiers: [...draft.tiers, { pkg_qty: "", corp_price: "", ed_govt_price: "", value_price: "" }] })} className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-blue-600"><Plus size={14} /> Add tier</button>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="px-4 h-10 rounded-xl font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
          <button onClick={onSave} disabled={!draft.category || !draft.name} className="px-4 h-10 rounded-xl bg-green-500 text-white font-bold flex items-center gap-2 disabled:opacity-40">Save</button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const INPUT_CLS = "w-full h-10 rounded-xl border-2 border-slate-200 focus:border-orange-500 outline-none px-3 text-sm";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="tc-eyebrow text-slate-500 mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}
